/**
 * @fileoverview Archivo con funciones y constantes utiles comunes a varias partes de la aplicacion.
 */

/**
 * Capitalizar un texto (primera letra de cada palabra en mayuscula)
 */
export function capitalizar(nombre) {
  if (!nombre) {
    return '';
  }
  return nombre.toLowerCase().replace(/(?:^|\s|-|"|'|\.|,)\S|(?<=')\S/g, (match) => match.toUpperCase());
}

/**
 * Formatear nombre de equipo de club (Categoría + Letra/Sufijo)
 * Ejemplo: ("Cde Toni Kroos Academy 'B'", "Cadetes") -> "Cadetes B"
 *          ("Cde Toni Kroos Academy", "Cadetes") -> "Cadetes"
 */
export const formatClubTeamName = (nombre, categoria) => {
  if (!categoria && !nombre) {
    return '';
  }
  if (!categoria) {
    return capitalizar(nombre);
  }

  const catCapitalizada = capitalizar(categoria);
  if (!nombre) {
    return catCapitalizada;
  }

  // Extraer sufijo (ej: "'A'", "'B'", "A", "B") al final del nombre
  const parts = nombre.trim().split(/\s+/);
  const lastPart = parts[parts.length - 1];

  // Si termina en una letra/código de equipo (1 o 2 caracteres, con o sin comillas)
  if (/^['"]?[a-zA-Z0-9]{1,2}['"]?$/.test(lastPart)) {
    const letra = lastPart.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    return `${catCapitalizada} '${letra}'`;
  }

  return catCapitalizada;
};

/**
 * Extrae el nombre del club a partir del nombre del equipo eliminando sufijos de letra/equipo (ej. "'A'", "'B'", "C").
 * Ejemplo: "Atletico Chopera Alcobendas 04 'D'" -> "Atletico Chopera Alcobendas 04"
 */
export const extraerNombreClub = (nombreEquipo) => {
  if (!nombreEquipo) {
    return '';
  }
  return nombreEquipo.replace(/\s+['"]?[a-zA-Z0-9]{1,2}['"]?$/, '').trim();
};

/**
 * Formatear nombre de liga (Categoria + Division + Grupo)
 */
export const formatLigaName = (categoria, division, grupo) => {
  return `${capitalizar(categoria)} · ${capitalizar(division)} · ${capitalizar(grupo)}`;
};

/**
 * Formatear nombre de jugador de manera corta y legible.
 * Ejemplo: ("Perez Garcia, Juan") -> "Juan Perez"
 *          ("Juan Perez Garcia") -> "Juan Perez"
 */
export const formatearNombre = (nombreCompleto) => {
  if (!nombreCompleto) {
    return '';
  }
  if (nombreCompleto.includes(',')) {
    const [apellidos, nombre] = nombreCompleto.split(',').map((s) => s.trim());
    const primerApellido = apellidos ? apellidos.split(' ')[0] : '';
    return `${capitalizar(nombre)} ${capitalizar(primerApellido)}`.trim();
  }
  const partes = nombreCompleto.trim().split(/\s+/);
  if (partes.length <= 2) {
    return capitalizar(nombreCompleto);
  }
  return capitalizar(`${partes[0]} ${partes[1]}`);
};

/**
 * Lateralidades
 */
export const lateralidades = ['', 'Zurdo', 'Diestro'];

/**
 * Demarcaciones
 */
export const demarcaciones = [
  '',
  'Portero',
  'Lateral Derecho',
  'Lateral Izquierdo',
  'Central',
  'Pivote',
  'Mediocentro',
  'Mediapunta',
  'Extremo Derecho',
  'Extremo Izquierdo',
  'Delantero Centro',
];

/**
 * Jerarquía de categorías (orden fijo)
 */
export const CATEGORIAS_ORDEN = [
  'SENIOR',
  'JUVENILES',
  'CADETES',
  'INFANTILES',
  'ALEVINES',
  'BENJAMINES',
  'PREBENJAMINES',
  'PREBENJAMIN',
  'DEBUTANTES',
];

/**
 * Ordenar equipos por categoría según CATEGORIAS_ORDEN
 */
export function ordenarPorCategoria(equipos) {
  if (!equipos || equipos.length === 0) {
    return [];
  }

  return [...equipos].sort((a, b) => {
    const categoriaA = a.categoria?.toUpperCase() || 'OTROS';
    const categoriaB = b.categoria?.toUpperCase() || 'OTROS';

    const indexA = CATEGORIAS_ORDEN.indexOf(categoriaA);
    const indexB = CATEGORIAS_ORDEN.indexOf(categoriaB);

    // Si ambas categorías están en el orden definido
    if (indexA !== -1 && indexB !== -1) {
      if (indexA !== indexB) {
        return indexA - indexB;
      }
      return (a.nombreEquipo || '').localeCompare(b.nombreEquipo || '');
    }

    // Si solo A está en el orden, A va primero
    if (indexA !== -1) {
      return -1;
    }

    // Si solo B está en el orden, B va primero
    if (indexB !== -1) {
      return 1;
    }

    // Si ninguna está en el orden, ordenar alfabéticamente por categoría y luego nombre
    const catComp = categoriaA.localeCompare(categoriaB);
    if (catComp !== 0) {
      return catComp;
    }
    return (a.nombreEquipo || '').localeCompare(b.nombreEquipo || '');
  });
}

/**
 * Ordenar una lista de objetos por un campo y dirección
 */
export function ordenarLista(lista, config) {
  if (!Array.isArray(lista) || !config?.field) {
    return lista || [];
  }

  const { field, direction } = config;

  return [...lista].sort((a, b) => {
    let valA = a[field];
    let valB = b[field];

    // Manejo de nulos/undefined
    if (valA === null || valA === undefined) {
      valA = '';
    }
    if (valB === null || valB === undefined) {
      valB = '';
    }

    // Cálculos especiales para jugadores
    if (field === 'porcentajeTitular') {
      valA = a.titularidades / (a.convocatorias || 1);
      valB = b.titularidades / (b.convocatorias || 1);
    }

    // Normalización para strings
    if (typeof valA === 'string') {
      valA = valA.toLowerCase();
    }
    if (typeof valB === 'string') {
      valB = valB.toLowerCase();
    }

    if (valA < valB) {
      return direction === 'asc' ? -1 : 1;
    }
    if (valA > valB) {
      return direction === 'asc' ? 1 : -1;
    }
    return 0;
  });
}

/**
 * Formatea una fecha TIMESTAMP a formato DD/MM/YYYY local
 */
export function formatearFecha(fechaString) {
  if (!fechaString) {
    return '-';
  }
  const fecha = new Date(fechaString);
  return fecha.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });
}

/**
 * Extraer las temporadas disponibles de un objeto que contiene misListas y otrasListas
 */
export function extraerTemporadasDisponiblesListas(data, temporadaActual) {
  if (!data) {
    return [];
  }
  const t = new Set();
  if (temporadaActual) {
    t.add(temporadaActual);
  }
  data.misListas?.forEach((l) => {
    if (l.temporada) {
      t.add(l.temporada);
    }
  });
  data.otrasListas?.forEach((l) => {
    if (l.temporada) {
      t.add(l.temporada);
    }
  });
  return Array.from(t).sort().reverse();
}

/**
 * Genera temporadas adyacentes a partir de una temporada base.
 * Muestra la anterior, la actual y las dos siguientes.
 * Ejemplo: para '2024-2025' genera ['2023-2024', '2024-2025', '2025-2026', '2026-2027']
 */
export function generarTemporadasAdyacentes(tempActual) {
  if (!tempActual) {
    return [];
  }
  try {
    const parts = tempActual.split('-');
    const year1 = parseInt(parts[0], 10);
    return [`${year1 - 1}-${year1}`, `${year1}-${year1 + 1}`, `${year1 + 1}-${year1 + 2}`, `${year1 + 2}-${year1 + 3}`];
  } catch {
    return [tempActual];
  }
}

/**
 * Obtiene las iniciales de un nombre completo (máximo 2 caracteres en mayúsculas).
 * @param {string} nombre - Nombre completo
 * @returns {string} Iniciales
 */
export function getInicialesNombre(nombre) {
  if (!nombre) {
    return '?';
  }
  return nombre
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

/**
 * Colapsa el formato de una temporada a 2 dígitos por año (ej. '2025-2026' -> '25-26').
 * @param {string} temporada - Temporada en formato completo
 * @returns {string} Temporada colapsada
 */
export function formatTemporadaColapsada(temporada) {
  if (!temporada) {
    return '';
  }
  const parts = temporada.split('-');
  if (parts.length === 2 && parts[0].length === 4 && parts[1].length === 4) {
    return `${parts[0].slice(-2)}-${parts[1].slice(-2)}`;
  }
  return temporada;
}

/**
 * Formatea una fecha para visualizaciones en tablas y tooltips.
 * @param {string|Date} fechaStr - Cadena o instancia de fecha
 * @returns {{ corta: string, completa: string }} Formato corto DD/MM y completo DD/MM/AAAA
 */
export function formatFecha(fechaStr) {
  if (!fechaStr) {
    return { corta: '', completa: '' };
  }
  const d = new Date(fechaStr);
  if (isNaN(d.getTime())) {
    return { corta: String(fechaStr), completa: String(fechaStr) };
  }
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const anio = d.getFullYear();
  return {
    corta: `${dia}/${mes}`,
    completa: `${dia}/${mes}/${anio}`,
  };
}

/**
 * Obtiene la paleta de colores y etiqueta de un resultado de partido (V, E, D).
 * @param {string} resultado - 'V', 'E', o 'D'
 * @returns {{ bg: string, color: string, border: string, label: string }}
 */
export function obtenerColorResultado(resultado) {
  if (resultado === 'V') {
    return { bg: '#dcfce7', color: '#15803d', border: '#86efac', label: 'Victoria' };
  }
  if (resultado === 'E') {
    return { bg: '#fef3c7', color: '#b45309', border: '#fde68a', label: 'Empate' };
  }
  if (resultado === 'D') {
    return { bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5', label: 'Derrota' };
  }
  return { bg: '#f1f5f9', color: '#64748b', border: '#cbd5e1', label: 'No disputado' };
}

const DIAS_SEMANA_ABR = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const DIAS_SEMANA_COMPLETOS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

/**
 * Formatea la fecha y hora de un partido para vistas de jornada y calendarios.
 * @param {string|Date} fechaStr
 * @returns {{ fecha: string, fechaAnio: string, hora: string, diaSem: string, diaSemCompleto: string, textoCorto: string, completa: string, timestamp: number }}
 */
export function formatFechaPartido(fechaStr) {
  if (!fechaStr) {
    return {
      fecha: '-',
      fechaAnio: '-',
      hora: '',
      diaSem: '',
      diaSemCompleto: '',
      textoCorto: '-',
      completa: '',
      timestamp: 0,
    };
  }

  let d = null;

  // 1. Si incluye AM/PM (formato estadounidense del JVM backend) o guiones (ISO/SQL YYYY-MM-DD)
  if (typeof fechaStr === 'string' && (fechaStr.includes('AM') || fechaStr.includes('PM') || fechaStr.includes('-'))) {
    const parsed = new Date(fechaStr);
    if (!isNaN(parsed.getTime())) {
      d = parsed;
    }
  }

  // 2. Si viene en formato español DD/MM/AAAA o DD/MM/YY (con posible hora y AM/PM)
  if (!d && typeof fechaStr === 'string') {
    const matchEs = fechaStr.match(
      /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:[,\s]+(\d{1,2}):(\d{2})(?::(\d{2}))?(?:\s*(AM|PM))?)?/i
    );
    if (matchEs) {
      const dia = parseInt(matchEs[1], 10);
      const mes = parseInt(matchEs[2], 10) - 1;
      let anio = parseInt(matchEs[3], 10);
      if (anio < 100) {
        anio += 2000;
      }
      let hora = matchEs[4] ? parseInt(matchEs[4], 10) : 0;
      const min = matchEs[5] ? parseInt(matchEs[5], 10) : 0;
      const ampm = matchEs[7] ? matchEs[7].toUpperCase() : null;
      if (ampm === 'PM' && hora < 12) {
        hora += 12;
      } else if (ampm === 'AM' && hora === 12) {
        hora = 0;
      }
      const parsed = new Date(anio, mes, dia, hora, min);
      if (!isNaN(parsed.getTime())) {
        d = parsed;
      }
    }
  }

  // 3. Intento general con Date nativo
  if (!d) {
    const parsed = new Date(fechaStr);
    if (!isNaN(parsed.getTime())) {
      d = parsed;
    }
  }

  if (d && !isNaN(d.getTime())) {
    const diaSem = DIAS_SEMANA_ABR[d.getDay()];
    const diaSemCompleto = DIAS_SEMANA_COMPLETOS[d.getDay()];
    const dia = String(d.getDate()).padStart(2, '0');
    const mes = String(d.getMonth() + 1).padStart(2, '0');
    const anio = d.getFullYear();
    const horas = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    const tieneHora = !(horas === '00' && mins === '00');
    const fecha = `${dia}/${mes}`;
    const fechaAnio = `${dia}/${mes}/${anio}`;
    const hora = tieneHora ? `${horas}:${mins}` : '';
    const textoCorto = hora ? `${diaSem} ${fecha} ${hora}` : `${diaSem} ${fecha}`;
    const completa = `${diaSem}, ${dia}/${mes}/${anio}${hora ? ` a las ${hora}` : ''}`;
    return {
      fecha,
      fechaAnio,
      hora,
      diaSem,
      diaSemCompleto,
      textoCorto,
      completa,
      timestamp: d.getTime(),
    };
  }

  // Fallback si no se puede parsear
  const str = String(fechaStr);
  return {
    fecha: str,
    fechaAnio: str,
    hora: '',
    diaSem: '',
    diaSemCompleto: '',
    textoCorto: str,
    completa: str,
    timestamp: 0,
  };
}

/**
 * Parsea una cadena de fecha de partido y devuelve su timestamp en milisegundos (o 0 si no es válida).
 * @param {string|Date} fechaStr
 * @returns {number}
 */
export function parseFechaTimestamp(fechaStr) {
  const info = formatFechaPartido(fechaStr);
  return info.timestamp || 0;
}

/**
 * Ordena partidos cronológicamente (primero el que se juegue antes).
 * @param {Array} partidos
 * @returns {Array}
 */
export function ordenarPartidosPorFecha(partidos) {
  if (!Array.isArray(partidos) || partidos.length === 0) {
    return [];
  }
  return [...partidos].sort((a, b) => {
    const timeA = parseFechaTimestamp(a.fecha);
    const timeB = parseFechaTimestamp(b.fecha);
    if (timeA && timeB && timeA !== timeB) {
      return timeA - timeB;
    }
    if (timeA && !timeB) {
      return -1;
    }
    if (!timeA && timeB) {
      return 1;
    }
    return (a.idPartido || 0) - (b.idPartido || 0);
  });
}
